import { apiClientes, apiSpmweb } from "../config/api";
import {
  mapApiResponsePFToBoleto,
  mapApiResponsePJToBoleto,
  BoletoApiResponsePF,
  BoletoApiResponsePJ,
} from "../pages/Acionar2ViaBoleto/Mensalidades/interfaces";
import { Response } from "../interfaces/services/Response";

interface DadosPF {
  numeroCartao: string;
  cpf: string;
  captchaToken: string;
}

interface DadosPJ {
  codigoEmpresa: string;
  cnpj: string;
  captchaToken: string;
}

export const getBoletosPessoaFisica = async (
  numeroCartao: string,
  cpf: string,
  captchaToken: string,
): Promise<Response | null> => {
  try {
    const response = await apiClientes.post(`financeiro/getboletocanalpf`, {
      cartao: numeroCartao,
      cpf: cpf,
      captchaToken: captchaToken,
    });

    if (response.data.status === "NOK") {
      throw new Error(response.data.mensagem || "Erro desconhecido");
    }

    const apiResponse = response.data as BoletoApiResponsePF;
    const boletosFormatados = mapApiResponsePFToBoleto(apiResponse, true);
    return {
      ...response,
      data: {
        sucesso: true,
        mensalidades: boletosFormatados,
      },
    };
  } catch (error: any) {
    throw error;
  }
};

export const getBoletosPessoaJuridica = async (
  codigoEmpresa: string,
  cnpj: string,
  captchaToken: string,
): Promise<Response | null> => {
  try {
    const response = await apiSpmweb.post(`wspm0121/boletos-em-aberto-portal`, {
      idContr: codigoEmpresa,
      cnpj: cnpj,
      captchaToken: captchaToken,
    });
    const apiResponse = response.data as BoletoApiResponsePJ;
    const boletosFormatados = mapApiResponsePJToBoleto(apiResponse, true);
    return {
      ...response,
      data: {
        sucesso: true,
        mensalidades: boletosFormatados,
      },
    };
  } catch (error: any) {
    throw error;
  }
};

interface DownloadBoletoParams {
  prefixo: string;
  titulo: string;
  parcela: string;
  idContrato: string;
}
export interface GenerateNfeLinkParams {
  numeroTitulo?: string;
  idFatura?: string | number;
  idContrato?: string;
}

export interface GenerateNfeLinkResponse {
  error?: string;
  link?: string;
}

const isValidNfeUrl = (value: unknown): value is string => {
  if (typeof value !== 'string' || !value.trim()) {
    return false;
  }

  const normalized = value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

  if (normalized.includes('titulo nao encontrado')) {
    return false;
  }

  try {
    const url = new URL(value.trim());
    return url.protocol === 'https:' || url.protocol === 'http:';
  } catch {
    return false;
  }
};

export const generateNfeLink = async (
  params: GenerateNfeLinkParams | string | number,
): Promise<GenerateNfeLinkResponse> => {
  try {
    const requestBody =
      typeof params === 'object'
        ? {
            ...(params.numeroTitulo
              ? { numero_titulo: params.numeroTitulo }
              : { id_fatura: Number(params.idFatura) }),
            ...(params.idContrato ? { id_contrato: params.idContrato } : {}),
          }
        : { numero_titulo: String(params) };

    const response = await apiSpmweb.post('wspm0121/link-nfe', requestBody);

    const data = response.data;

    if (!data || data.status !== 'OK') {
      return {
        error: data?.mensagem || 'Não foi possível gerar o link da NFe.',
        link: undefined,
      };
    }

    const link =
      typeof data.dados === 'string'
        ? data.dados
        : data?.dados?.link || data?.dados?.value;

    // A API pode retornar status "OK" com uma mensagem em "dados"
    // (ex.: "Titulo não encontrado para geração de NF."). Nesse caso não há NFe.
    if (!isValidNfeUrl(link)) {
      return {
        error: typeof link === 'string' && link.trim() ? link : 'Link da NFe não disponível.',
        link: undefined,
      };
    }

    return {
      error: undefined,
      link: link.trim(),
    };
  } catch (error) {
    console.error('Erro ao gerar link da NFe:', error);

    return {
      error: 'Houve um problema ao gerar o link da NFe. Tente novamente mais tarde.',
      link: undefined,
    };
  }
};

export const downloadBoletoPF = async (
  base64: string,
  filename?: string,
): Promise<void> => {
  try {
    const link = document.createElement("a");
    link.href = `data:application/pdf;base64,${base64}`;
    link.download = filename || "boleto.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    throw error;
  }
};

export const downloadBoletoPJ = async (
  params: DownloadBoletoParams,
): Promise<void> => {
  try {
    const response = await apiSpmweb.get(`wspm0121/segunda-via-boleto-portal`, {
      params: {
        prefixo: params.prefixo,
        titulo: params.titulo,
        parcela: params.parcela,
        idContrato: params.idContrato,
      },
    });

    if (
      response.data.dados.boleto &&
      response.data.dados.boleto[0] &&
      response.data.dados.boleto[0].arquivo_base64
    ) {
      const boletoData = response.data.dados.boleto[0];
      const link = document.createElement("a");
      link.href = `data:application/pdf;base64,${boletoData.arquivo_base64}`;
      link.download =
        boletoData.nome_arquivo ||
        `boleto-${params.prefixo}-${params.titulo}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      throw new Error("Arquivo base64 não encontrado na resposta da API");
    }
  } catch (error: any) {
    throw error;
  }
};

export const downloadBoleto = async (
  boleto: any,
  codigoEmpresa?: string,
  isPessoaFisica: boolean = false,
): Promise<void> => {
  try {
    if (isPessoaFisica) {
      boleto.base64 = boleto.base64.split(",")[1];

      if (boleto.base64) {
        await downloadBoletoPF(boleto.base64, `boleto-${boleto.id}.pdf`);
      } else {
        throw new Error("Base64 do boleto não encontrado");
      }
    } else {
      if (!codigoEmpresa) {
        throw new Error(
          "Código da empresa é obrigatório para download de boleto PJ",
        );
      }

      const params: DownloadBoletoParams = {
        prefixo: boleto.prefixo_titulo || boleto.id.split("-")[0],
        titulo: boleto.numero_titulo || boleto.id.split("-")[1],
        parcela: boleto.parcela_titulo || boleto.id.split("-")[2],
        idContrato: codigoEmpresa,
      };

      await downloadBoletoPJ(params);
    }
  } catch (error) {
    throw error;
  }
};

export const getSegundaViaExterno = async (
  dados: DadosPF | DadosPJ,
): Promise<Response | null> => {
  try {
    if ("cpf" in dados && "numeroCartao" in dados) {
      return await getBoletosPessoaFisica(
        dados.numeroCartao,
        dados.cpf,
        dados.captchaToken,
      );
    } else if ("cnpj" in dados && "codigoEmpresa" in dados) {
      return await getBoletosPessoaJuridica(
        dados.codigoEmpresa,
        dados.cnpj,
        dados.captchaToken,
      );
    } else {
      throw new Error("Dados inválidos fornecidos");
    }
  } catch (error: any) {
    throw error;
  }
};

